import { isDev, type ServerResponse } from '@core/utils';
import { isNil } from 'es-toolkit';
import { useRef } from 'react';
import { toast as toastifyToast } from 'react-toastify';
import SvgSnackbarCheck from '../../__generated__/icon/SvgSnackbarCheck';
import SvgSnackbarCircle from '../../__generated__/icon/SvgSnackbarCircle';
import SvgSnackbarWarning from '../../__generated__/icon/SvgSnackbarWarning';
import SvgWarningTriangle from '../../__generated__/icon/SvgWarningTriangle';
export interface ToastOptions {
  type?: 'fail' | 'success' | 'warning' | 'info';
  message: string;
  holdTime?: number;
  /** 토스트 위치 offset (px) */
  offset?: {
    top?: number;
    bottom?: number;
  };
}
interface ToastErrorOptions extends Omit<ToastOptions, 'message'> {
  error: unknown;
  exceptionOptions?: {
    [code: number]: string;
  };
}
const toastQueue = new Map<string, ToastOptions>();
let activeTopOffsetToastCount = 0;
let activeBottomOffsetToastCount = 0;
export const useToast = () => {
  const toast = ({
    message,
    holdTime = 3_000,
    type = 'success',
    offset
  }: Omit<ToastOptions, 'toastKey' | 'createdAt'>) => {
    const iconConfig = {
      success: {
        Component: SvgSnackbarCheck,
        className: 'size-20',
        originalFill: true
      },
      fail: {
        Component: SvgSnackbarWarning,
        className: 'size-20',
        originalFill: true
      },
      warning: {
        Component: SvgWarningTriangle,
        className: 'size-20 text-Y-500',
        originalFill: false
      },
      info: {
        Component: SvgSnackbarCircle,
        className: 'size-20',
        originalFill: true
      }
    } as const;
    const {
      Component: Icon,
      className: iconClassName,
      originalFill
    } = iconConfig[type];
    const trimMessage = message.trim();
    const toastKey = `${trimMessage}-${type}`;

    //* 빈 토스트 방지
    if (trimMessage === '') {
      return;
    }
    if (toastQueue.has(toastKey)) {
      return;
    }
    toastQueue.set(toastKey, {
      message: trimMessage,
      holdTime,
      type
    });
    if (!isNil(offset?.top)) {
      activeTopOffsetToastCount += 1;
      document.documentElement.style.setProperty('--toast-top-offset', `${offset.top}px`);
    }
    if (!isNil(offset?.bottom)) {
      activeBottomOffsetToastCount += 1;
      document.documentElement.style.setProperty('--toast-bottom-offset', `${offset.bottom}px`);
    }
    const cleanupTimer = setTimeout(() => {
      toastQueue.delete(toastKey);
    }, holdTime + 500);
    toastifyToast(trimMessage, {
      autoClose: holdTime,
      icon: <Icon className={iconClassName} originalFill={originalFill} />,
      onClose: () => {
        clearTimeout(cleanupTimer);
        toastQueue.delete(toastKey);
        //* offset 변수는 컨테이너 전역이라 offset 토스트가 모두 닫혔을 때만 제거
        if (!isNil(offset?.top)) {
          activeTopOffsetToastCount = Math.max(0, activeTopOffsetToastCount - 1);
          if (activeTopOffsetToastCount === 0) {
            document.documentElement.style.removeProperty('--toast-top-offset');
          }
        }
        if (!isNil(offset?.bottom)) {
          activeBottomOffsetToastCount = Math.max(0, activeBottomOffsetToastCount - 1);
          if (activeBottomOffsetToastCount === 0) {
            document.documentElement.style.removeProperty('--toast-bottom-offset');
          }
        }
      }
    });
  };
  const {
    current
  } = useRef({
    toast,
    toastError: ({
      error,
      type: type_0 = 'fail',
      exceptionOptions,
      ...restOptions
    }: ToastErrorOptions) => {
      const {
        return_code: errorCode,
        return_message
      } = error as ServerResponse;
      const errorMessage = return_message?.trim();
      const toastErrorMessage = (message_0: string) => toast({
        ...restOptions,
        type: type_0,
        message: message_0
      });
      if (!errorCode || !errorMessage) {
        return toastErrorMessage('서버로 부터 정상적인 응답을 받지 못했습니다.');
      }
      const exceptedErrorMessage = exceptionOptions?.[errorCode];
      if (exceptedErrorMessage) {
        return toastErrorMessage(exceptedErrorMessage);
      }

      //TODO: 30000 이상 에러 메시지 백엔드와 논의 재필요
      if (30000 <= errorCode) {
        return toastErrorMessage(errorMessage);
      }
      return toastErrorMessage(`일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요. (${errorCode})`);
    },
    devToast: (message_1: string) => {
      if (isDev) {
        toast({
          message: message_1,
          type: 'warning'
        });
      }
    }
  });
  return current;
};